import { API } from '@/config/constants'
import { useAccountStore } from '@/stores/account-store'

/**
 * Write this migrated user's edge-identity row ({newSub → genesis}) BEFORE any
 * strict, access-token call — otherwise the resolver 503s (the new sub has no row
 * yet, and it can't be pre-seeded because the sub is unknowable until first login).
 *
 * Authenticated by the ID TOKEN, not the access token: only the id token carries
 * `custom:edge_user_id` (the genesis). Idempotent server-side (conditional put).
 * Retries a few times — this gates app entry, so a transient blip shouldn't strand
 * the user. Throws if it ultimately can't provision.
 */
export async function provisionMigratedUser(idToken: string, attempts = 3): Promise<void> {
  let lastErr: unknown
  for (let i = 0; i < attempts; i++) {
    let res: Response
    try {
      res = await fetch(`${API.users}/v2/provision-migrated-user`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
      })
    } catch (e) {
      lastErr = e // network error → retry
      continue
    }
    if (res.ok) return
    // 401 (bad token) won't fix itself — fail fast (outside the try, so it propagates);
    // 5xx may be transient, so record and retry.
    if (res.status < 500) throw new Error(`provision-migrated-user: ${res.status}`)
    lastErr = new Error(`provision-migrated-user: ${res.status}`)
  }
  throw lastErr instanceof Error ? lastErr : new Error('provision-migrated-user failed')
}

// One in-flight/settled promise per sub, so `use()` gets a STABLE reference (a fresh
// promise each render would suspend forever) and the network call fires exactly once
// per page load.
const provisionCache = new Map<string, Promise<void>>()

/**
 * Idempotent, memoized wrapper for `use()` in a Suspense boundary: provisions the
 * migrated user's identity row, then forces a token refresh so the now-cleared
 * `custom:migration_stage` propagates and the migration gate goes false — the server
 * (Cognito attr), not client state, is the source of truth, so this stops firing across
 * reloads and devices once it succeeds. Rejections are swallowed so `use()` resolves
 * rather than throwing to an error boundary — the app proceeds and the first strict call
 * surfaces any real problem, instead of the whole tree crashing.
 */
export function ensureMigratedUserProvisioned(sub: string, idToken: string): Promise<void> {
  let p = provisionCache.get(sub)
  if (!p) {
    p = provisionMigratedUser(idToken)
      .then(async () => {
        // The backend cleared custom:migration_stage. Refresh via the store's single refresh
        // action so the reactive session picks up the cleared attr and the migration gate goes
        // false — one call, no service/store drift.
        await useAccountStore.getState().refreshSession()
      })
      .catch((e) => {
        // Left un-cleared → retries on the next load, which is the desired behavior.
        console.error('identity provisioning failed', e)
      })
    provisionCache.set(sub, p)
  }
  return p
}
